import { Schema } from "@effect/schema";
import { UsdFromBigInt, makeUsd, Usd, USD_DECIMALS } from "@silo/types";
import { pipe } from "effect";
import { formatUnits } from "viem";
import { toLowerCase } from "effect/String";
import {
  parseCompactNotation,
  parseNumber,
  parseUsd,
  trimNegativeZero,
} from "./string";

export type UsdFromDecimalString = typeof UsdFromDecimalString.Type;
export type UsdFromDecimalStringEncoded = typeof UsdFromDecimalString.Encoded;

export const UsdFromDecimalString = Schema.transform(
  Schema.String,
  UsdFromBigInt,
  {
    decode: parseUsd,
    encode: (value) => {
      return formatUnits(value, USD_DECIMALS);
    },
  }
).annotations({ identifier: "UsdFromDecimalString" });

export type UsdFromDecimal = typeof UsdFromDecimal.Type;
export type UsdFromDecimalEncoded = typeof UsdFromDecimal.Encoded;

/**
 * Decodes a `Usd` value from a number
 *
 * ```ts
 * // $1.25
 * const encoded = 1.25
 *
 * const decoded = Schema.decodeSync(UsdFromDecimal)(encoded) // => 1_250_000n
 * ```
 */
export const UsdFromDecimal = Schema.transform(
  Schema.Number,
  UsdFromDecimalString,
  {
    decode: String,
    encode: Number,
  }
).annotations({ identifier: "UsdFromDecimal" });

const fullUSDFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const singleCentsFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumSignificantDigits: 2,
});

export function asFullUsd(value: Usd): string {
  const num = pipe(formatUnits(value, USD_DECIMALS), parseNumber);

  /**
   * For values less than $0.10, format using significant digits so that those
   * values do not appear as $0.
   */
  if (Math.abs(num) < 0.1 && num !== 0) return singleCentsFormatter.format(num);

  return fullUSDFormatter.format(num);
}

export function asFullUsdValue(value: Usd): Usd {
  return pipe(value, asFullUsd, parseUsd);
}

const wholeUSDFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export function asWholeUsd(value: Usd): string {
  return pipe(
    formatUnits(value, USD_DECIMALS),
    parseNumber,
    (num) => wholeUSDFormatter.format(num),
    trimNegativeZero
  );
}

export function asWholeUsdValue(value: Usd): Usd {
  return pipe(value, asWholeUsd, parseUsd);
}

const compactUsdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
});

export function asCompactUsd(value: Usd): string {
  return pipe(
    formatUnits(value, USD_DECIMALS),
    parseNumber,
    (num) => {
      const abs = Math.abs(num);

      if (abs >= 999.5) {
        const absValueToFormat = Math.max(abs, 1000);

        return compactUsdFormatter.format(Math.sign(num) * absValueToFormat);
      }

      return wholeUSDFormatter.format(num);
    },
    trimNegativeZero,
    toLowerCase
  );
}

export function asCompactUsdValue(value: Usd): Usd {
  const formatted = asCompactUsd(value);

  return pipe(parseCompactNotation(formatted, USD_DECIMALS), makeUsd);
}
